import { RequestStatus } from 'features/request/requestSlice';
import type {
    ContractObjectCreateInput,
    ContractObjectUpdateInput,
    ContractSubObjectCreateInput,
    ContractSubObjectUpdateInput,
    WorkOrderObjectSelectionData,
} from 'gql/graphql';
import type {
    IUseWorkOrderEditObjectDataSourceProps,
    IUseWorkOrderEditObjectDataSourceResult,
} from 'pages/Project/Common/WorkOrderStructure/types';

import { useCallback, useEffect, useMemo } from 'react';

import { useUerCreateContractSubObject } from './useUerCreateContractSubObject';
import { useUerCreateWorkOrderObject } from './useUerCreateWorkOrderObject';
import { useUerCreateWorkOrderSubObject } from './useUerCreateWorkOrderSubObject';
import { useUerIsUniqueContractObjectCode } from './useUerIsUniqueContractObjectCode';
import { useUerIsUniqueContractObjectName } from './useUerIsUniqueContractObjectName';
import { useUerIsUniqueContractSubObjectName } from './useUerIsUniqueContractSubObjectName';
import { useUerUpdateContractObject } from './useUerUpdateContractObject';
import { useUerUpdateContractSubObject } from './useUerUpdateContractSubObject';
import { useUerUpdateWorkOrderObject } from './useUerUpdateWorkOrderObject';
import { useUerUpdateWorkOrderSubObject } from './useUerUpdateWorkOrderSubObject';

const nameKey = 'name';
const codeKey = 'codeWBS';
const rationaleKey = 'rationale';

export const useUerWorkOrderEditObjectDataSource = (
    props: IUseWorkOrderEditObjectDataSourceProps<WorkOrderObjectSelectionData>,
): IUseWorkOrderEditObjectDataSourceResult => {
    const {
        clearErrors,
        setError,
        formState,
        watch,
        constructionTypeId,
        contractId,
        workOrderOrChangeId,
        onCreateSuccess,
        onUpdateSuccess,
        objectId,
        initialCode,
        isContractObject,
        parentObjectToAppend,
        checkedIds,
    } = props;

    const name = watch?.(nameKey);
    const code = watch?.(codeKey);
    const rationale = watch?.(rationaleKey);
    const isDirtyObjectName = formState?.dirtyFields?.name;
    const isDirtyObjectCode = formState?.dirtyFields?.codeWBS;
    const isClearingCode = !!initialCode?.trim() && !code?.trim();

    const { result: isObjectNameUnique, uerIsUniqueContractObjectNameStatus } = useUerIsUniqueContractObjectName({
        contractId,
        constructionTypeId,
        name: !parentObjectToAppend ? name : '',
        isDirtyObjectName,
    });

    const { result: isSubObjectNameUnique, uerIsUniqueContractSubObjectNameStatus } =
        useUerIsUniqueContractSubObjectName({
            name: parentObjectToAppend ? name : '',
            isDirtyObjectName,
            contractObjectId: parentObjectToAppend?.id,
            contractSubObjectId: objectId,
        });

    const {
        result: isObjectCodeUnique,
        isNotUnique: isObjectCodeNotUnique,
        isCheckError,
    } = useUerIsUniqueContractObjectCode({
        code,
        isDirtyObjectCode,
        contractId,
        contractObjectId: objectId,
    });

    const { uerCreateWorkOrderObjectStatus, uerCreateWorkOrderObjectRequestMakeRequest } = useUerCreateWorkOrderObject(
        workOrderOrChangeId,
        onCreateSuccess,
    );

    const { uerUpdateWorkOrderObjectStatus, uerUpdateWorkOrderObjectMakeRequest } = useUerUpdateWorkOrderObject(
        workOrderOrChangeId,
        onUpdateSuccess,
    );

    const { uerCreateWorkOrderSubObjectStatus, uerCreateWorkOrderSubObjectRequestMakeRequest } =
        useUerCreateWorkOrderSubObject(workOrderOrChangeId, onCreateSuccess);

    const { uerUpdateWorkOrderSubObjectStatus, uerUpdateWorkOrderSubObjectMakeRequest } =
        useUerUpdateWorkOrderSubObject(workOrderOrChangeId, onCreateSuccess);

    const { uerCreateContractSubObjectStatus, uerCreateContractSubObjectMakeRequest } = useUerCreateContractSubObject(
        onCreateSuccess,
        contractId,
    );

    const { uerUpdateContractSubObjectStatus, uerUpdateContractSubObjectMakeRequest } = useUerUpdateContractSubObject(
        contractId,
        onUpdateSuccess,
    );

    const { status: uerUpdateContractObjectStatus, makeRequest: uerUpdateContractObjectMakeRequest } =
        useUerUpdateContractObject(contractId, onUpdateSuccess);

    const doCreateRequest = useCallback(
        (formData: any) => {
            if (!constructionTypeId) {
                return;
            }

            if (parentObjectToAppend) {
                const input: ContractSubObjectCreateInput = {
                    name: formData.name,
                    rationale: formData.rationale,
                    parentId: parentObjectToAppend.id,
                    codeWBS: formData.codeWBS?.trim(),
                };

                if (checkedIds?.includes(parentObjectToAppend.id)) {
                    uerCreateWorkOrderSubObjectRequestMakeRequest(input);
                } else {
                    uerCreateContractSubObjectMakeRequest(input);
                }

                return;
            }

            const input: ContractObjectCreateInput = {
                constructionTypeId,
                contractId,
                name: formData.name,
                rationale: formData.rationale,
                codeWBS: formData.codeWBS?.trim(),
            };

            uerCreateWorkOrderObjectRequestMakeRequest(input);
        },
        [
            checkedIds,
            constructionTypeId,
            contractId,
            parentObjectToAppend,
            uerCreateContractSubObjectMakeRequest,
            uerCreateWorkOrderObjectRequestMakeRequest,
            uerCreateWorkOrderSubObjectRequestMakeRequest,
        ],
    );

    const doUpdateRequest = useCallback(
        (formData: any) => {
            if (parentObjectToAppend) {
                const input: ContractObjectUpdateInput | ContractSubObjectUpdateInput = {
                    id: objectId,
                    name: formData.name,
                    rationale: formData.rationale,
                    codeWBS: formData.codeWBS?.trim(),
                };

                if (isContractObject) {
                    uerUpdateContractSubObjectMakeRequest(input);
                } else if (checkedIds?.includes(parentObjectToAppend.id)) {
                    uerUpdateWorkOrderSubObjectMakeRequest(input);
                } else {
                    uerUpdateContractSubObjectMakeRequest(input);
                }

                return;
            }

            const input: ContractObjectUpdateInput = {
                id: objectId,
                name: formData.name,
                rationale: formData.rationale,
                codeWBS: formData.codeWBS?.trim(),
            };

            if (isContractObject) {
                uerUpdateContractObjectMakeRequest(input);
            } else {
                uerUpdateWorkOrderObjectMakeRequest(input);
            }
        },
        [
            checkedIds,
            isContractObject,
            objectId,
            parentObjectToAppend,
            uerUpdateContractObjectMakeRequest,
            uerUpdateContractSubObjectMakeRequest,
            uerUpdateWorkOrderObjectMakeRequest,
            uerUpdateWorkOrderSubObjectMakeRequest,
        ],
    );

    const isNotUnique = useMemo(() => {
        if (
            !parentObjectToAppend &&
            uerIsUniqueContractObjectNameStatus === RequestStatus.SUCCESS &&
            isDirtyObjectName &&
            !isObjectNameUnique
        ) {
            return true;
        }

        return !!(
            parentObjectToAppend &&
            uerIsUniqueContractSubObjectNameStatus &&
            isDirtyObjectName &&
            !isSubObjectNameUnique
        );
    }, [
        isDirtyObjectName,
        isObjectNameUnique,
        isSubObjectNameUnique,
        parentObjectToAppend,
        uerIsUniqueContractObjectNameStatus,
        uerIsUniqueContractSubObjectNameStatus,
    ]);

    const btnConfirmDisabled = useMemo(
        () =>
            !formState?.isDirty ||
            !name ||
            (!isContractObject && !rationale) ||
            isClearingCode ||
            isNotUnique ||
            !isObjectCodeUnique ||
            uerCreateWorkOrderObjectStatus === RequestStatus.FETCHING ||
            uerUpdateWorkOrderObjectStatus === RequestStatus.FETCHING ||
            uerCreateWorkOrderSubObjectStatus === RequestStatus.FETCHING ||
            uerUpdateWorkOrderSubObjectStatus === RequestStatus.FETCHING ||
            uerCreateContractSubObjectStatus === RequestStatus.FETCHING ||
            uerUpdateContractSubObjectStatus === RequestStatus.FETCHING ||
            uerUpdateContractObjectStatus === RequestStatus.FETCHING,
        [
            formState?.isDirty,
            isNotUnique,
            isObjectCodeUnique,
            isContractObject,
            isClearingCode,
            name,
            rationale,
            uerCreateContractSubObjectStatus,
            uerCreateWorkOrderObjectStatus,
            uerCreateWorkOrderSubObjectStatus,
            uerUpdateContractSubObjectStatus,
            uerUpdateContractObjectStatus,
            uerUpdateWorkOrderObjectStatus,
            uerUpdateWorkOrderSubObjectStatus,
        ],
    );

    useEffect(() => {
        if (name && isNotUnique) {
            setError?.(nameKey, {
                message: `${parentObjectToAppend ? 'Подобъект' : 'Объект'} с таким наименованием уже существует`,
            });
        } else {
            clearErrors?.(nameKey);
        }
    }, [clearErrors, name, isNotUnique, setError, parentObjectToAppend]);

    useEffect(() => {
        if (isClearingCode) {
            setError?.(codeKey, { message: 'Существующий код нельзя очистить' });
        } else if (code && isObjectCodeNotUnique) {
            setError?.(codeKey, {
                message: `${parentObjectToAppend ? 'Подобъект' : 'Объект'} с таким кодом уже существует`,
            });
        } else if (isCheckError) {
            setError?.(codeKey, { message: 'Не удалось проверить уникальность кода' });
        } else {
            clearErrors?.(codeKey);
        }
    }, [clearErrors, code, isClearingCode, isObjectCodeNotUnique, isCheckError, parentObjectToAppend, setError]);

    return {
        doCreateRequest,
        doUpdateRequest,
        btnConfirmDisabled,
    };
};
